/**
 * Icon for a meeting's conferencing platform.
 *
 * Walks the candidate list from `getMeetingIconSources` — our own brand mark,
 * then the provider's icon, then a favicon resolved from the join URL's host —
 * stepping down on each load failure and landing on a generic video glyph when
 * nothing resolves. That ladder is what gives long-tail providers a real icon
 * instead of a nondescript camera.
 *
 * Each rung declares how it must be drawn, because the three sources have very
 * different contrast guarantees:
 *
 *  - `tint` — a monochrome mark of ours (Luma). Drawn as a mask filled
 *    with `currentColor` so it inherits the row's text colour and stays legible
 *    in both themes. An <img> cannot inherit currentColor, which is why this
 *    path exists at all.
 *  - `chip` — third-party art we cannot recolour. Sits on a neutral surface, so
 *    a near-black favicon doesn't vanish against a dark row.
 *  - neither — our own multi-colour brand art (Zoom, Meet, Teams, Goldcast),
 *    which is designed to read on any background.
 */

import { VideoRecorder } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getMeetingIconSources,
  getMeetingPlatformLabel,
} from '@/lib/crm/record/utils/meetingHelpers';
import { cx } from '@/utils/cx';

interface MeetingProviderIconProps {
  /** The meeting's join URL. No icon renders without one. */
  meetLink?: string;
  /** Provider's own name for the solution, when the calendar supplied one. */
  providerName?: string;
  /** Provider-hosted icon URL, when the calendar supplied one. */
  providerIconUrl?: string;
  className?: string;
}

const FALLBACK_ICON_CLASSNAME =
  'text-neutral-400 dark:text-neutral-500 shrink-0';

/** Neutral surface for third-party art of unknown contrast. */
const CHIP_CLASSNAME =
  'shrink-0 rounded-sm bg-primary p-px ring-1 ring-inset ring-black/10 dark:ring-white/20';

/**
 * A mask-rendered monochrome mark. The mask URL is per-provider data, so the
 * `style` prop is isolated here rather than scattered across callers — the
 * dynamic-value exception in FRONTEND_PATTERNS § Color Tokens.
 */
function TintedMark({
  src,
  label,
  className,
}: {
  src: string;
  label: string;
  className?: string;
}) {
  const maskStyle = useMemo(
    () => ({
      maskImage: `url(${src})`,
      WebkitMaskImage: `url(${src})`,
      maskSize: 'contain',
      WebkitMaskSize: 'contain',
      maskRepeat: 'no-repeat',
      WebkitMaskRepeat: 'no-repeat',
      maskPosition: 'center',
      WebkitMaskPosition: 'center',
    }),
    [src]
  );

  return (
    <span
      role='img'
      aria-label={label}
      className={cx('shrink-0 bg-current', className)}
      style={maskStyle}
    />
  );
}

export function MeetingProviderIcon({
  meetLink,
  providerName,
  providerIconUrl,
  className,
}: MeetingProviderIconProps) {
  const sources = useMemo(
    () => getMeetingIconSources(meetLink, providerIconUrl),
    [meetLink, providerIconUrl]
  );
  const label = useMemo(
    () => getMeetingPlatformLabel(meetLink, providerName),
    [meetLink, providerName]
  );

  const [failedCount, setFailedCount] = useState(0);

  // A new meeting (or a re-resolved icon) starts the ladder over, otherwise a
  // row recycled by the list would inherit the previous meeting's failures.
  useEffect(() => {
    setFailedCount(0);
  }, [sources]);

  const handleError = useCallback(() => {
    setFailedCount(previous => previous + 1);
  }, []);

  const source = sources[failedCount];

  // Track whether the current source image has loaded so the timeout does not
  // advance the ladder after a successful load.
  const loadedRef = useRef(false);

  // Advance the ladder if the current source has a timeout and neither onLoad
  // nor onError has fired within it. This guards against a slow external host
  // (the provider icon or the favicon) stalling the row indefinitely — onError
  // only fires on a failure response, not on a connection hang.
  useEffect(() => {
    loadedRef.current = false;
    if (!source?.timeoutMs) {
      return;
    }
    const id = setTimeout(() => {
      if (!loadedRef.current) {
        setFailedCount(previous => previous + 1);
      }
    }, source.timeoutMs);
    return () => clearTimeout(id);
  }, [source]);

  const handleLoad = useCallback(() => {
    loadedRef.current = true;
  }, []);

  if (!source) {
    // Either the meeting has no join link at all, or every candidate failed.
    return meetLink ? (
      <VideoRecorder
        className={cx(FALLBACK_ICON_CLASSNAME, className)}
        aria-label={`${label} meeting`}
      />
    ) : null;
  }

  if (source.tint) {
    return (
      <TintedMark
        src={source.src}
        label={`${label} meeting`}
        className={className}
      />
    );
  }

  return (
    <img
      src={source.src}
      alt={`${label} meeting`}
      className={cx(source.chip ? CHIP_CLASSNAME : 'shrink-0', className)}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
}
